// File: /opt/stacks/workout/app/src/hooks/useWorkoutSettings.js
//
// localStorage-persisted workout preferences with optional API sync.
// Mirrors useAudioSettings.js pattern — plain module with a synchronous getter
// so consumers (useWakeLock, etc.) can read settings without React overhead.

import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "khronos_workout_settings";
const CHANGE_EVENT = "khronos:workout-settings-changed";

export const WORKOUT_DEFAULTS = {
  wakeLockEnabled: true,
  skipLastRest: true,
  // What a rep-to-timed changeover gets: "wait" holds on the Ready gate until
  // you tap (the original behaviour, and the default — it is the only option
  // that never starts a clock while you are still walking to the next
  // station), "count" gives the ordinary count-in, "off" goes straight in.
  changeoverHold: "wait",
  drainTimer: false,
  drainSegments: false,
  pauseOnMacroTap: true,
  defaultTransitionSeconds: 15,
  defaultBlockSetupSeconds: 45,
  // Show the "Connect Bike" prompt during bike-relevant workouts.
  showConnectTrainer: true,
  // Auto-switch the visible Coach/Equipment pane to match the current phase's
  // modality (bike/rower phase → equipment pane, coach/strength → Coach pane).
  // Client-only (not API-synced) — a per-device viewing preference.
  autoFollowPhasePane: true,
  // Idle gap (minutes) after the last recorded telemetry before the
  // interrupted-workout recovery banner is offered. Brief app switches stay
  // below this so they don't nag mid-session. Client-only (not API-synced).
  recoveryIdleThresholdMinutes: 10,
  // Intelligent session guidance (issue #9): feed a summary of recent completed
  // sessions into the generation prompt so the model varies the stimulus and
  // avoids overtraining. historyLookbackDays is the lookback window.
  guidedSessionEnabled: true,
  historyLookbackDays: 7,
  // Who sees a workout once you finish it (issue #79): "off" | "friends" |
  // "public". The feed only ever held posts someone deliberately created, so
  // a mate could train all month and leave no trace in it. Finishing a session
  // now posts it; this is the control over that.
  shareCompletedWorkouts: "friends",
  // Badges on the Mates tab and "you earned a badge" notifications (#170).
  // Off still records them, so switching back on shows the history.
  showBadges: true,
  // Show me on my mates' weekly board (Settings › Badges). Off until switched
  // on; hidden anyway while badges are off.
  showOnBoard: false,
  // Free training's Rest button: seconds to count down from, or null to
  // count up until you're ready. The last one picked. Client-only.
  freeRestTarget: null,
  // Coach screen orientation during a workout (#149): "auto" turns with the
  // phone (landscape for a phone mounted on a rower), "portrait" keeps it
  // upright. Client-only: it's about how this phone is held.
  coachOrientation: "auto",
  // Basic / Advanced mode (#172): "basic" tucks rarely needed options away
  // behind their defaults, "advanced" shows everything. New people start on
  // Basic; anyone with settings saved from before keeps Advanced (see
  // getWorkoutSettings, and migration 064 on the account side).
  uiMode: "basic",
  // Tag each session with the weather where you trained (Open-Meteo, from the
  // phone's rough location). Client-only: location permission is per phone.
  weatherOnSessions: false,
};

export const WORKOUT_SYNC_FIELDS = [
  "wakeLockEnabled",
  "skipLastRest",
  "changeoverHold",
  "drainTimer",
  "drainSegments",
  "pauseOnMacroTap",
  "defaultTransitionSeconds",
  "defaultBlockSetupSeconds",
  "showConnectTrainer",
  "guidedSessionEnabled",
  "historyLookbackDays",
  "shareCompletedWorkouts",
  "showBadges",
  "showOnBoard",
  "uiMode",
];

// Fields that intentionally live only on this device and are NOT synced to the
// account (no API column). The sync-coverage test asserts every default is
// either synced or listed here, so a new default can't silently go untracked.
export const WORKOUT_CLIENT_ONLY_FIELDS = [
  "recoveryIdleThresholdMinutes",
  "autoFollowPhasePane",
  "freeRestTarget",
  "coachOrientation",
  "weatherOnSessions",
];

/** Read workout settings from localStorage (synchronous, ~0.01ms). */
export function getWorkoutSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      // Settings saved before the mode existed belong to someone who has been
      // using the app: they keep everything they could see (#172).
      return { ...WORKOUT_DEFAULTS, uiMode: "advanced", ...saved };
    }
  } catch {
    // corrupted data — fall back to defaults
  }
  return { ...WORKOUT_DEFAULTS };
}

/** Write workout settings to localStorage. */
function saveWorkoutSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage full or unavailable — fail silently
  }
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: settings }));
  } catch {
    // non-browser environment — ignore
  }
}

export const WORKOUT_SETTINGS_CHANGE_EVENT = CHANGE_EVENT;

/**
 * Merge server-fetched settings into localStorage.
 * Server values win over local values.
 */
export function mergeWorkoutSettingsFromServer(serverSettings) {
  if (!serverSettings) return;
  const current = getWorkoutSettings();
  const merged = { ...current };

  if (serverSettings.wake_lock_enabled != null)
    merged.wakeLockEnabled = serverSettings.wake_lock_enabled;
  if (serverSettings.skip_last_rest != null)
    merged.skipLastRest = serverSettings.skip_last_rest;
  if (serverSettings.changeover_hold != null)
    merged.changeoverHold = serverSettings.changeover_hold;
  if (serverSettings.drain_timer != null)
    merged.drainTimer = serverSettings.drain_timer;
  if (serverSettings.drain_segments != null)
    merged.drainSegments = serverSettings.drain_segments;
  if (serverSettings.pause_on_macro_tap != null)
    merged.pauseOnMacroTap = serverSettings.pause_on_macro_tap;
  if (serverSettings.default_transition_seconds != null)
    merged.defaultTransitionSeconds = serverSettings.default_transition_seconds;
  if (serverSettings.default_block_setup_seconds != null)
    merged.defaultBlockSetupSeconds = serverSettings.default_block_setup_seconds;
  if (serverSettings.show_connect_trainer != null)
    merged.showConnectTrainer = serverSettings.show_connect_trainer;
  if (serverSettings.guided_session_enabled != null)
    merged.guidedSessionEnabled = serverSettings.guided_session_enabled;
  if (serverSettings.history_lookback_days != null)
    merged.historyLookbackDays = serverSettings.history_lookback_days;
  if (serverSettings.share_completed_workouts != null)
    merged.shareCompletedWorkouts = serverSettings.share_completed_workouts;
  if (serverSettings.show_badges != null)
    merged.showBadges = serverSettings.show_badges;
  if (serverSettings.show_on_board != null)
    merged.showOnBoard = serverSettings.show_on_board;
  if (serverSettings.ui_mode === "basic" || serverSettings.ui_mode === "advanced")
    merged.uiMode = serverSettings.ui_mode;

  saveWorkoutSettings(merged);
  return merged;
}

/**
 * Convert local workout settings to API field names for syncing.
 */
export function workoutSettingsToApi(settings) {
  return {
    wake_lock_enabled: settings.wakeLockEnabled,
    skip_last_rest: settings.skipLastRest,
    changeover_hold: settings.changeoverHold,
    drain_timer: settings.drainTimer,
    drain_segments: settings.drainSegments,
    pause_on_macro_tap: settings.pauseOnMacroTap,
    default_transition_seconds: settings.defaultTransitionSeconds,
    default_block_setup_seconds: settings.defaultBlockSetupSeconds,
    show_connect_trainer: settings.showConnectTrainer,
    guided_session_enabled: settings.guidedSessionEnabled,
    history_lookback_days: settings.historyLookbackDays,
    share_completed_workouts: settings.shareCompletedWorkouts,
    show_badges: settings.showBadges,
    show_on_board: settings.showOnBoard,
    ui_mode: settings.uiMode,
  };
}

/**
 * React hook for workout settings UI.
 * Returns [settings, updateSettings] where updateSettings accepts a partial update.
 * Optional onSync callback is called with API-shaped data on each change.
 */
const useWorkoutSettings = ({ onSync } = {}) => {
  const [settings, setSettings] = useState(getWorkoutSettings);

  useEffect(() => {
    const handler = (e) => {
      const next = e?.detail || getWorkoutSettings();
      setSettings((prev) => {
        const merged = { ...prev, ...next };
        const changed = Object.keys(merged).some((k) => merged[k] !== prev[k]);
        return changed ? merged : prev;
      });
    };
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, []);

  const updateSettings = useCallback(
    (partial) => {
      setSettings((prev) => {
        const next = { ...prev, ...partial };
        saveWorkoutSettings(next);
        onSync?.(workoutSettingsToApi(next));
        return next;
      });
    },
    [onSync],
  );

  return [settings, updateSettings];
};

export default useWorkoutSettings;
